import { appConfig } from "@/commons";
import type { CurrentUser } from "@/types/auth";

const LEGACY_AUTH_STORAGE_KEYS = [
  "authToken",
  "userRole",
  "gameooUser",
  "game",
  "react-admin-auth",
] as const;

const SESSION_CACHE_TTL_MS = 30_000;

let cachedUser: CurrentUser | null = null;
let cachedUntil = 0;
let pendingUserRequest: Promise<CurrentUser> | null = null;
let cacheGeneration = 0;

type CurrentUserResponse =
  | CurrentUser
  | { user?: CurrentUser; data?: CurrentUser };

const createAuthError = (status: number, message = "Authentication required") =>
  ({
    status,
    redirectTo: "/login",
    message: status === 401 || status === 403 ? false : message,
  });

const normalizeCurrentUser = (response: CurrentUserResponse): CurrentUser => {
  const wrapped = response as { user?: CurrentUser; data?: CurrentUser };
  const user = wrapped.user ?? wrapped.data ?? (response as CurrentUser);

  if (user?.id == null || !user.pseudo || !user.role) {
    throw createAuthError(500, "Invalid response from /auth/me");
  }

  return {
    ...user,
    role: user.role.toLowerCase(),
    fullName: user.fullName ?? user.full_name ?? user.fullname ?? user.pseudo,
  };
};

const requestCurrentUser = async (): Promise<CurrentUser> => {
  const response = await fetch(`${appConfig.api.baseUrl}/auth/me`, {
    method: "GET",
    cache: "no-store",
    headers: {
      Accept: "application/json",
    },
    credentials: "include",
  });

  if (response.status === 401 || response.status === 403) {
    throw createAuthError(response.status);
  }

  if (!response.ok) {
    throw createAuthError(response.status, "Unable to retrieve current user");
  }

  return normalizeCurrentUser((await response.json()) as CurrentUserResponse);
};

export const getCurrentUser = async ({
  force = false,
}: { force?: boolean } = {}): Promise<CurrentUser> => {
  if (force) {
    cacheGeneration += 1;
    cachedUser = null;
    cachedUntil = 0;
    pendingUserRequest = null;
  }

  if (!force && cachedUser && Date.now() < cachedUntil) {
    return cachedUser;
  }

  if (!force && pendingUserRequest) {
    return pendingUserRequest;
  }

  const requestGeneration = cacheGeneration;
  const currentRequest = requestCurrentUser()
    .then((user) => {
      if (requestGeneration === cacheGeneration) {
        cachedUser = user;
        cachedUntil = Date.now() + SESSION_CACHE_TTL_MS;
      }
      return user;
    })
    .catch((error) => {
      if (requestGeneration === cacheGeneration) {
        cachedUser = null;
        cachedUntil = 0;
      }
      throw error;
    })
    .finally(() => {
      if (pendingUserRequest === currentRequest) {
        pendingUserRequest = null;
      }
    });

  pendingUserRequest = currentRequest;
  return currentRequest;
};

export const clearCurrentUser = (): void => {
  cacheGeneration += 1;
  cachedUser = null;
  cachedUntil = 0;
  pendingUserRequest = null;
};

export const clearLegacyAuthStorage = (): void => {
  if (typeof window === "undefined") return;

  const clearStorage = (storage: Storage) => {
    const prefixedKeys = Array.from({ length: storage.length }, (_, index) =>
      storage.key(index),
    ).filter((key): key is string =>
      Boolean(key?.startsWith("react-admin-auth")),
    );

    for (const key of [...LEGACY_AUTH_STORAGE_KEYS, ...prefixedKeys]) {
      storage.removeItem(key);
    }
  };

  try {
    clearStorage(window.localStorage);
  } catch {
    // Storage may be unavailable in hardened/private browser contexts.
  }

  try {
    clearStorage(window.sessionStorage);
  } catch {
    // Storage may be unavailable in hardened/private browser contexts.
  }
};
